/**
 * UserGradeModal Component
 * 사용자 등급 변경 모달
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';
import { Button } from '../atoms/Button/Button';

interface UserGradeModalProps {
  isOpen: boolean;
  user: {
    uid: string;
    displayName: string;
    email: string;
    memberGrade: string;
  } | null;
  onClose: () => void;
  onConfirm: (uid: string, newGrade: string) => Promise<void>;
}

const GRADES = [
  { value: 'member', label: '일반회원', description: '' },
  { value: 'regular', label: '정회원', description: '' },
  { value: 'special', label: '특별회원', description: '' }
];

export const UserGradeModal: React.FC<UserGradeModalProps> = ({
  isOpen,
  user,
  onClose,
  onConfirm
}) => {
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !user) return null;

  const handleConfirm = async () => {
    if (!selectedGrade) {
      alert('변경할 등급을 선택해주세요.');
      return;
    }

    if (selectedGrade === user.memberGrade) {
      alert('현재 등급과 동일합니다.');
      return;
    }

    try {
      setLoading(true);
      await onConfirm(user.uid, selectedGrade);
      onClose();
    } catch (error: any) {
      console.error('Failed to update user grade:', error);
      alert('등급 변경에 실패했습니다: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <Modal>
        <Header>
          <Title>회원 등급 변경</Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </Header>

        <Content>
          <UserInfo>
            <UserName>{user.displayName}</UserName>
            <UserEmail>{user.email}</UserEmail>
            <CurrentGrade>
              현재 등급: <GradeBadge>{user.memberGrade}</GradeBadge>
            </CurrentGrade>
          </UserInfo>

          <GradeSelection>
            <SelectLabel>변경할 등급을 선택하세요</SelectLabel>
            {GRADES.map(grade => (
              <GradeOption
                key={grade.value}
                $isSelected={selectedGrade === grade.value}
                onClick={() => setSelectedGrade(grade.value)}
              >
                <GradeOptionHeader>
                  <Radio $isSelected={selectedGrade === grade.value} />
                  <GradeLabel>{grade.label}</GradeLabel>
                </GradeOptionHeader>
                {grade.description && (
                  <GradeDescription>{grade.description}</GradeDescription>
                )}
              </GradeOption>
            ))}
          </GradeSelection>
        </Content>

        <Footer>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading || !selectedGrade}
          >
            {loading ? '처리 중...' : '변경하기'}
          </Button>
        </Footer>
      </Modal>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${tokens.colors.neutral[0]};
  border-radius: ${tokens.borderRadius.xl};
  box-shadow: ${tokens.shadows['2xl']};
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const Header = styled.div`
  padding: ${tokens.spacing[6]};
  border-bottom: 1px solid ${tokens.colors.neutral[200]};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: ${tokens.typography.fontSize['2xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${tokens.typography.fontSize['3xl']};
  color: ${tokens.colors.neutral[500]};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color ${tokens.animation.duration.fast};

  &:hover {
    color: ${tokens.colors.neutral[800]};
  }
`;

const Content = styled.div`
  padding: ${tokens.spacing[6]};
  overflow-y: auto;
  flex: 1;
`;

const UserInfo = styled.div`
  background: ${tokens.colors.neutral[50]};
  border-radius: ${tokens.borderRadius.md};
  padding: ${tokens.spacing[4]};
  margin-bottom: ${tokens.spacing[6]};
`;

const UserName = styled.div`
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[1]};
`;

const UserEmail = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
  margin-bottom: ${tokens.spacing[3]};
`;

const CurrentGrade = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
  display: flex;
  align-items: center;
  gap: ${tokens.spacing[2]};
`;

const GradeBadge = styled.span`
  background: ${tokens.colors.primary[500]};
  color: ${tokens.colors.neutral[0]};
  padding: ${tokens.spacing[1]} ${tokens.spacing[2]};
  border-radius: ${tokens.borderRadius.base};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.semibold};
`;

const GradeSelection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[3]};
`;

const SelectLabel = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
  margin-bottom: ${tokens.spacing[2]};
`;

const GradeOption = styled.div<{ $isSelected: boolean }>`
  border: 2px solid ${props => props.$isSelected ? tokens.colors.primary[500] : tokens.colors.neutral[300]};
  background: ${props => props.$isSelected ? `${tokens.colors.primary[500]}10` : tokens.colors.neutral[0]};
  border-radius: ${tokens.borderRadius.md};
  padding: ${tokens.spacing[4]};
  cursor: pointer;
  transition: all ${tokens.animation.duration.fast};

  &:hover {
    border-color: ${tokens.colors.primary[500]};
  }
`;

const GradeOptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing[3]};
  margin-bottom: ${tokens.spacing[2]};
`;

const Radio = styled.div<{ $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.$isSelected ? tokens.colors.primary[500] : tokens.colors.neutral[400]};
  border-radius: 50%;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${tokens.colors.primary[500]};
    opacity: ${props => props.$isSelected ? 1 : 0};
    transition: opacity ${tokens.animation.duration.fast};
  }
`;

const GradeLabel = styled.div`
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
`;

const GradeDescription = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
  padding-left: 32px;
`;

const Footer = styled.div`
  padding: ${tokens.spacing[6]};
  border-top: 1px solid ${tokens.colors.neutral[200]};
  display: flex;
  gap: ${tokens.spacing[3]};
  justify-content: flex-end;
`;
